/**
 * imageTransform — Supabase Storage image transformation helpers.
 *
 * Supabase Storage supports on-the-fly image resizing via query
 * params on public URLs.  Rather than a bespoke thumbnail-
 * generation pipeline (edge function + sharp + upload variants),
 * we just append `?width=…` and Supabase serves a cached variant.
 * Requires a paid plan (Pro+) to enable transformation on the
 * bucket; if disabled the URL still works (browser fetches the
 * original), so callers are safe either way.
 *
 * ── THE PILL BUG (GAL-14), read before touching any variant ──────
 * Supabase's transformer defaults to `resize=cover`, and with cover
 * a width-only request does NOT scale proportionally: it keeps the
 * ORIGINAL height and crops the sides. Measured on production:
 * a 3072×4096 photo requested as `?width=800` came back 800×4096 —
 * a vertical sliver of the middle 26% of the frame. Every grid,
 * lightbox and hero image in the app was that sliver, which is why
 * gallery tiles rendered as "pills" (the editor measured the
 * sliver's 0.2 ratio) and the designs looked "zoomed in" (a sliver
 * cover-fitted into a normal frame). Two rounds of layout patches
 * in JustifiedGallery could never fix it because the pixels were
 * wrong before layout began.
 *
 * `resize=contain` with a width-only request scales proportionally
 * (800×1067 for that same photo; 400×283 for a landscape logo —
 * both verified). So: EVERY variant here carries an explicit
 * `resize` mode, and only `thumbnailUrl` is allowed to crop.
 *
 * Variants:
 *   • thumbnailUrl(url) — 400×400 cover, list rows + hub covers
 *                         (a deliberate square crop)
 *   • gridUrl(url)      — 800 wide, aspect preserved, gallery tiles
 *   • previewUrl(url)   — 1200 wide, aspect preserved, delivery hero
 *   • lightboxUrl(url)  — 2000 wide, aspect preserved
 *   • heroUrl(url)      — 2400 wide, aspect preserved, cover photo
 *   • originalUrl(url)  — untouched, downloads + fullscreen
 */

/**
 * Append Supabase image-transformation params to a public URL.
 * If the URL is already parametrized, we don't touch it (avoids
 * double-appending on re-renders).  If it's not a Supabase URL,
 * we return it as-is.
 */
function withTransform(url: string, params: string): string {
  if (!url) return url;
  if (url.includes('?')) return url; // already has params — respect them
  if (!url.includes('/storage/v1/object/public/')) return url;
  // Supabase's image transform endpoint is `/render/image/public/`
  // instead of `/object/public/`.  Swap the path prefix + append.
  const transformed = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/',
  );
  return `${transformed}?${params}`;
}

/** 400×400 cover — gallery grid + list rows. */
export function thumbnailUrl(url: string): string {
  return withTransform(url, 'width=400&height=400&resize=cover&quality=78');
}

/** 1200 wide, aspect preserved — lightbox + delivery hero. */
export function previewUrl(url: string): string {
  return withTransform(url, 'width=1200&resize=contain&quality=82');
}

/**
 * ~800 wide, aspect ratio PRESERVED (no height/cover crop) — the
 * justified-rows client gallery grid. The square `thumbnailUrl`
 * variant distorts row math, so the grid uses this instead.
 */
export function gridUrl(url: string): string {
  return withTransform(url, 'width=800&resize=contain&quality=80');
}

/** ~2000 wide, aspect preserved — full-screen client lightbox. */
export function lightboxUrl(url: string): string {
  return withTransform(url, 'width=2000&resize=contain&quality=85');
}

/** ~2400 wide, aspect preserved — the full-bleed gallery hero cover. */
export function heroUrl(url: string): string {
  return withTransform(url, 'width=2400&resize=contain&quality=80');
}

/** Untouched — downloads + fullscreen. */
export function originalUrl(url: string): string {
  return url;
}
